import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, Facebook, Twitter, Instagram, Linkedin, Youtube, Search, Menu, X, LogIn, ChevronDown, Megaphone, ChevronRight, ArrowRight } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { useAuth } from "../../lib/auth";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About",
    children: [
      { label: "About Society", to: "/about" },
      { label: "Mission & Vision", to: "/mission-vision" },
      { label: "History", to: "/history" },
      { label: "Objectives", to: "/objectives" },
    ],
  },
  {
    label: "Organization",
    children: [
      { label: "Organization Structure", to: "/executive-committee" },
    ],
  },
  {
    label: "Members",
    children: [
      { label: "Members Directory", to: "/members" },
      { label: "Become a Member", to: "/become-member" },
    ],
  },
  {
    label: "News & Events",
    children: [
      { label: "Announcements", to: "/announcements" },
      { label: "Upcoming Events", to: "/events" },
      { label: "CME Programs", to: "/cme-programs" },
    ],
  },
  {
    label: "Media",
    children: [
      { label: "Gallery", to: "/gallery" },
      { label: "Downloads", to: "/downloads" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

const ANNOUNCE = [
  "GOS CME Meeting on Advancements in Glaucoma — 25 July 2026",
  "Membership Registration & Renewal for GOS is Now Open",
  "Visit the GOS website regularly for the latest news, announcements, and academic updates.",
  "Stay informed about upcoming CME programs, conferences, workshops, and society events.",
];

function MarqueeBar() {
  const items = [...ANNOUNCE, ...ANNOUNCE];
  return (
    <div className="bg-gradient-to-r from-brand-primary via-[#004a94] to-brand-primary text-white">
      <div className="container-wide flex items-center gap-4 py-2.5">
        <div className="hidden sm:flex items-center gap-2 shrink-0 pr-4 border-r border-white/20 text-[11px] uppercase tracking-[0.25em] font-semibold">
          <Megaphone size={14} className="text-brand-accent" /> Society News
        </div>
        <div className="marquee flex-1 min-w-0">
          <div className="marquee-track">
            {items.map((t, i) => (
              <div key={i} className="flex items-center gap-3 pr-12 text-[13px] whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-accent" />
                <span className="opacity-95">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = React.useState("");
  const nav = useNavigate();
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [open]);
  React.useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/search?q=${encodeURIComponent(q)}`); onClose(); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-brand-dark/85 backdrop-glass flex items-start justify-center pt-32 px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
        <div className="flex items-center gap-3 bg-white rounded-2xl px-6 py-5 shadow-2xl">
          <Search size={22} className="text-brand-primary shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search announcements, events, members…" className="flex-1 bg-transparent outline-none text-lg text-brand-text placeholder:text-slate-400" data-testid="header-search-input" />
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-lg hover:bg-brand-bg text-brand-muted"><X size={18} /></button>
        </div>
        <div className="mt-3 text-center text-white/60 text-xs uppercase tracking-[0.28em]">Press Enter to search · Esc to close</div>
      </form>
    </div>
  );
}

export default function Header() {
  const s = useSettings();
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(null);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="bg-brand-dark text-white/85 text-[12px]">
        <div className="container-wide flex items-center justify-between py-2.5">
          <div className="flex items-center gap-6">
            <a href={`tel:${s.phone || ""}`} className="hidden sm:flex items-center gap-2 hover:text-brand-secondary transition-colors" data-testid="topbar-phone">
              <Phone size={12} className="text-brand-secondary" /> <span>{s.phone}</span>
            </a>
            <a href={`mailto:${s.email || ""}`} className="flex items-center gap-2 hover:text-brand-secondary transition-colors" data-testid="topbar-email">
              <Mail size={12} className="text-brand-secondary" /> <span>{s.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden lg:flex items-center gap-2.5">
              {[["facebook", Facebook], ["twitter", Twitter], ["instagram", Instagram], ["linkedin", Linkedin], ["youtube", Youtube]].map(([k, Ico]) =>
                s[k] ? (
                  <a key={k} href={s[k]} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-white/5 hover:bg-brand-secondary/90 flex items-center justify-center transition-colors" aria-label={k}>
                    <Ico size={11} />
                  </a>
                ) : null
              )}
            </div>
            <div className="hidden md:block h-4 w-px bg-white/15" />
            {user && user.role === "admin" ? (
              <Link to="/admin" className="hidden md:inline-flex items-center gap-1.5 text-white hover:text-brand-secondary transition-colors font-medium" data-testid="header-admin-btn">
                Admin Panel <ArrowRight size={12} />
              </Link>
            ) : (
              <Link to="/admin/login" className="hidden md:inline-flex items-center gap-1.5 text-white hover:text-brand-secondary transition-colors font-medium" data-testid="header-login-btn">
                <LogIn size={12} /> Admin Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Marquee announcement bar */}
      <MarqueeBar />

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-glass shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)] border-b border-white/50" : "bg-white border-b border-brand-border"}`}>
        <div className="container-wide flex items-center justify-between gap-6 h-[104px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4 group shrink-0" data-testid="logo-link">
            <img src="/gos-logo.png" alt="GOS Logo" className="h-16 w-16 object-contain group-hover:scale-105 transition-transform duration-500" />
            <div className="hidden md:block leading-tight">
              <div className="font-display font-extrabold text-brand-text text-[18px] tracking-tight">Ghaziabad Ophthalmological Society</div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brand-primary font-semibold mt-1">Est. {s.established_year || 1983} · Advancing Eye Care</div>
            </div>
          </Link>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 flex-1 justify-center">
            {NAV.map((item) => item.children ? (
              <div key={item.label} className="relative" onMouseEnter={() => setOpenMenu(item.label)} onMouseLeave={() => setOpenMenu(null)}>
                <button className={`nav-link inline-flex items-center gap-1.5 px-3.5 xl:px-4 text-[14px] whitespace-nowrap ${openMenu === item.label ? "text-brand-primary" : ""} ${item.children.some((c) => isActive(c.to)) ? "active" : ""}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
                  {item.label} <ChevronDown size={13} className={`transition-transform duration-300 ${openMenu === item.label ? "rotate-180" : ""}`} />
                </button>
                {openMenu === item.label && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[240px] z-40">
                    <div className="bg-white shadow-[0_25px_60px_-15px_rgba(0,0,0,0.18)] rounded-2xl border border-brand-border/60 py-2 overflow-hidden">
                      {item.children.map((c) => (
                        <NavLink key={c.to} to={c.to} className={({isActive}) => `flex items-center justify-between px-5 py-3 text-[14px] transition-all group ${isActive ? "text-brand-primary bg-brand-primary-light font-semibold" : "text-brand-text hover:text-brand-primary hover:bg-brand-bg"}`}>
                          <span>{c.label}</span>
                          <ChevronRight size={13} className="opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} className={({isActive}) => `nav-link px-3.5 xl:px-4 text-[14px] whitespace-nowrap ${isActive ? "active" : ""}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className="w-11 h-11 rounded-full border border-brand-border hover:border-brand-primary hover:bg-brand-primary hover:text-white text-brand-primary flex items-center justify-center transition-all hover:-translate-y-0.5"
              aria-label="Search"
              data-testid="header-search-btn"
            >
              <Search size={17} />
            </button>
            <Link to="/become-member" className="hidden 2xl:inline-flex btn-primary !py-3 !px-6 text-[13px]" data-testid="header-cta-btn">
              Become a Member <ArrowRight size={15} />
            </Link>
            <Link to="/become-member" className="hidden md:inline-flex 2xl:hidden w-11 h-11 rounded-full bg-brand-primary text-white items-center justify-center hover:bg-brand-primary-hover hover:-translate-y-0.5 transition-all shadow-[0_8px_20px_-8px_rgba(0,86,166,0.5)]" aria-label="Become a Member" title="Become a Member" data-testid="header-cta-btn">
              <ArrowRight size={17} />
            </Link>
            <button className="lg:hidden p-2 -mr-1" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-brand-border bg-white max-h-[70vh] overflow-y-auto">
            <div className="container-wide py-4 space-y-1">
              {NAV.flatMap((item) => item.children ? item.children : [item]).map((c) => (
                <NavLink key={c.to} to={c.to} onClick={() => setMobileOpen(false)} className={({isActive}) => `block px-4 py-2.5 rounded-lg text-[14px] font-medium ${isActive ? "bg-brand-primary-light text-brand-primary" : "text-brand-text hover:bg-brand-bg"}`}>
                  {c.label}
                </NavLink>
              ))}
              <div className="pt-2 mt-2 border-t border-brand-border">
                {user && user.role === "admin" ? (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 rounded-lg text-[14px] font-medium text-brand-primary">Admin Panel</Link>
                ) : (
                  <Link to="/admin/login" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 rounded-lg text-[14px] font-medium text-brand-primary">Admin Login</Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
